/**
 * Reports management page.
 * Tabbed list with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { ReportsDataTable } from '@/components/reports/inperson/ReportsDataTable';
import { EditReportView } from '@/components/reports/inperson/EditReportView';
import { CreateReportView } from '@/components/reports/inperson/CreateReportView';
import { SelectReportTypeModal } from '@/components/reports/inperson/SelectReportTypeModal';
import { usePageActions } from '@/hooks/usePageActions';
import { useDetailsView } from '@/hooks/useDetailsView';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { getReport } from '@/services/inperson/reports.service';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/forms/Button';
import { X } from 'lucide-react';
import {
  REPORT_IN_PERSON_TAB_LABELS as TAB_LABELS,
  REPORT_IN_PERSON_TAB_TO_TYPES as TAB_TO_TYPES,
  REPORT_IN_PERSON_TAB_COLORS as TAB_COLORS,
  type ReportInPersonTab as Tab,
  SESSION_TYPE_CONFIG,
} from '@/components/reports/report-configs';
import { toReportInPersonTab as toTab } from '@/components/reports/inperson/report-table-utils';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'edit' | 'create' | null;

/** Tab ids in display order (RBAC subset is sorted back into this sequence). */
const TAB_ORDER: Tab[] = ['tutoring', 'information-meeting'];

/** In-person reports list; `?view=` / group deep-links pin a subset until cleared. */
export const ReportsManagementPage: React.FC = () => {
  const { canCreate, creatableTypes: creatableTypesRaw } = usePageActions('reports');
  const { elements, isLoading: elementsLoading } = useInterfaceElements('reports');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editReportData, setEditReportData] = React.useState<{
    sessionId: string;
    reportId: string;
    sessionType?: string;
  } | null>(null);
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode } = useViewMode('reports-management');
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [, setSearchParams] = useSearchParams();
  const [deepLink, setDeepLink] = React.useState<{
    reportId: string;
    sessionId: string;
  } | null>(null);
  const [deepLinkGroup, setDeepLinkGroup] = React.useState<{
    groupId: string;
    label?: string;
  } | null>(null);
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [selectedReportType, setSelectedReportType] = React.useState<Tab | null>(null);

  /** RBAC-allowed tabs; `undefined` while elements load so the table does not query with the wrong types. */
  const allowedTabIds = React.useMemo((): Tab[] | undefined => {
    if (elementsLoading) return undefined;
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const otherTabs: Tab[] =
      tabElements.length === 0
        ? ['tutoring', 'information-meeting']
        : tabElements.map((el) => toTab(el.elementId)).filter((id): id is Tab => id !== null);
    return [...otherTabs].sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
  }, [elements, elementsLoading]);

  /** Restore the last tab on browser back (`POP`); otherwise default to tutoring. */
  const [activeTab, setActiveTab] = React.useState<Tab>(() => {
    if (navigationType === 'POP') {
      const saved = toTab(sessionStorage.getItem('dt-tab:reports') ?? '');
      if (saved) return saved;
    }
    return 'tutoring';
  });

  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:reports', activeTab);
  }, [activeTab]);

  /** Deep-link: pin a single report via `?view=` + `?sessionId=`, then strip the params. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    const sessionIdParam = searchParams.get('sessionId');

    if (viewId && sessionIdParam) {
      setOpenRow(viewId);
      setDeepLink({ reportId: viewId, sessionId: sessionIdParam });
      setActiveForm(null);
      setEditReportData(null);
    } else if (viewId) {
      setOpenRow(viewId);
      setActiveForm(null);
      setEditReportData(null);
    }

    if (viewId || sessionIdParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          next.delete('sessionId');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, closeRow, setOpenRow, setSearchParams]);

  const {
    data: deepLinkReport,
    isLoading: isLoadingDeepLinkReport,
    isError: isDeepLinkError,
    error: deepLinkError,
  } = useQuery({
    queryKey: ['reports', 'deep-link', deepLink?.sessionId, deepLink?.reportId],
    queryFn: () => getReport(deepLink!.sessionId, deepLink!.reportId),
    enabled: !!deepLink,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isDeepLinkError,
    error: deepLinkError,
    catalogKey: 'reportNotFound',
    onClose: closeRow,
    entityId: deepLink?.reportId,
  });

  /** Drop the pinned deep-link report when the user closes the details row. */
  const prevOpenRowIdRef = React.useRef(openRowId);
  React.useEffect(() => {
    if (prevOpenRowIdRef.current !== null && openRowId === null) {
      setDeepLink(null);
    }
    prevOpenRowIdRef.current = openRowId;
  }, [openRowId]);

  /** Return to the full list after a notification deep-link. */
  const clearDeepLink = React.useCallback(() => {
    setDeepLink(null);
    closeRow();
  }, [closeRow]);

  /** Return to the full list after a group-filtered deep-link. */
  const clearDeepLinkGroup = React.useCallback(() => {
    setDeepLinkGroup(null);
    closeRow();
  }, [closeRow]);

  /** Close the form and drop edit / create-type state. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditReportData(null);
    setSelectedReportType(null);
  }, []);

  /** Types creatable from `action-create-*` RBAC; falls back to allowed tabs. */
  const creatableTabIds = React.useMemo((): Tab[] => {
    const TAB_IDS = new Set<Tab>(['tutoring', 'information-meeting']);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is Tab => TAB_IDS.has(id as Tab));
    }
    return (allowedTabIds ?? []) as Tab[];
  }, [creatableTypesRaw, allowedTabIds]);

  /** Skip the type modal when only one report type is creatable. */
  const handleCreateClick = React.useCallback(() => {
    if (creatableTabIds.length === 1) {
      setSelectedReportType(creatableTabIds[0]);
      setActiveForm('create');
      setTypeModalOpen(false);
      closeRow();
    } else {
      setTypeModalOpen(true);
      setEditReportData(null);
      closeRow();
    }
  }, [creatableTabIds, closeRow]);

  /** After the type modal: open the create form for that report type. */
  const handleTypeSelect = (type: Tab) => {
    setSelectedReportType(type);
    setActiveForm('create');
    setTypeModalOpen(false);
    closeRow();
  };

  /** Swap the table for the edit form and close any open details row. */
  const handleEditReport = React.useCallback(
    (sessionId: string, reportId: string, sessionType?: string) => {
      setEditReportData({ sessionId, reportId, sessionType });
      setActiveForm('edit');
      closeRow();
    },
    [closeRow],
  );

  /** Deep-link: edit a report or filter a group via location.state, then clear it. */
  React.useEffect(() => {
    const state = location.state as {
      editReport?: { sessionId: string; reportId: string; sessionType?: string };
      reportsForGroup?: { groupId: string; label?: string };
    } | null;
    if (state?.editReport) {
      const { sessionId, reportId, sessionType } = state.editReport;
      handleEditReport(sessionId, reportId, sessionType);
      void navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.reportsForGroup) {
      setDeepLinkGroup(state.reportsForGroup);
      setDeepLink(null);
      setActiveForm(null);
      setEditReportData(null);
      closeRow();
      void navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, handleEditReport, closeRow]);

  /** Scroll the page to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  /** Close the form and refresh in-person report queries. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditReportData(null);
    setSelectedReportType(null);
    void queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, [queryClient]);

  /** Pick the collapse-wrapper form from `activeForm`. */
  const formContent = React.useMemo(() => {
    if (activeForm === 'edit' && editReportData) {
      return (
        <EditReportView
          key={editReportData.reportId}
          sessionId={editReportData.sessionId}
          reportId={editReportData.reportId}
          sessionType={editReportData.sessionType}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    if (activeForm === 'create' && selectedReportType) {
      return (
        <CreateReportView
          reportType={selectedReportType}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editReportData, selectedReportType, closeForm, handleFormSuccess]);

  /** Hide the tab bar when RBAC leaves only one report type. */
  const showTabs = (allowedTabIds ?? []).length > 1;

  const tabItems = React.useMemo(
    () =>
      (allowedTabIds ?? []).map((id) => {
        const sessionTypesForTab = TAB_TO_TYPES[id];
        const firstType = sessionTypesForTab?.[0];
        const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;
        return {
          id,
          label: TAB_LABELS[id],
          icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
          color: TAB_COLORS[id],
        };
      }),
    [allowedTabIds],
  );

  /** Full list, or a pinned notification / group subset until the user clears it. */
  const renderListContent = () => {
    if (deepLinkGroup) {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-2 text-sm">
            <span>
              Comptes rendus de la séance
              {deepLinkGroup.label ? ` — ${deepLinkGroup.label}` : ''} (un compte rendu par groupe).
            </span>
            <Button variant="ghost" size="sm" onClick={clearDeepLinkGroup}>
              <X className="mr-1 h-4 w-4" /> Voir tous les comptes rendus
            </Button>
          </div>
          <ReportsDataTable
            deepLinkSessionGroupId={deepLinkGroup.groupId}
            expandedRowId={openRowId}
            onToggleRow={openRow}
            onCloseDetails={closeRow}
            onEditReport={handleEditReport}
            enableFilters={false}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </div>
      );
    }

    if (deepLink) {
      const deepLinkBody =
        isLoadingDeepLinkReport && !deepLinkReport ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <ReportsDataTable
            reports={deepLinkReport ? [deepLinkReport] : []}
            expandedRowId={openRowId}
            onToggleRow={openRow}
            onCloseDetails={closeRow}
            onEditReport={handleEditReport}
            enableFilters={false}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        );

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-2 text-sm">
            <span>Vous consultez un compte rendu spécifique (ouvert depuis une notification).</span>
            <Button variant="ghost" size="sm" onClick={clearDeepLink}>
              <X className="mr-1 h-4 w-4" /> Voir tous les comptes rendus
            </Button>
          </div>
          {deepLinkBody}
        </div>
      );
    }

    return (
      <ReportsDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditReport={handleEditReport}
        enableFilters
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        activeTab={activeTab}
        allowedTabIds={allowedTabIds}
      />
    );
  };

  return (
    <ManagementPage
      title="Gestion des Comptes Rendus"
      subtitle="Gérez et modifiez tous vos comptes rendus de séances"
      contentRef={contentRef}
      tabs={
        showTabs
          ? {
              items: tabItems,
              activeTab,
              onTabChange: (id) => {
                closeForm();
                closeRow();
                setActiveTab(id as Tab);
              },
            }
          : undefined
      }
      actions={
        canCreate ? (
          <Button preset="create-entity" onClick={handleCreateClick}>
            Créer un compte rendu
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        {renderListContent()}
      </CollapseReplaceWrapper>

      <SelectReportTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={handleTypeSelect}
        allowedTabIds={creatableTabIds}
      />
    </ManagementPage>
  );
};
